import { Colors, Text } from '@amelio/ui-library';

import { COMPENSATION_CAPTION } from '../compensationStyles';
import { LevelIcon } from './LevelIcon';

import type { CompensationLevel, CompensationLevelSummary } from '@/types/compensation';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getLocalizedText } from '@/utils/localization';
import { formatCurrency } from '@/utils/numberFormat';

interface LevelSummaryTableProps {
    levels: CompensationLevel[];
    levelSummary: CompensationLevelSummary[];
}

/**
 * What the current strategy adds up to, level by level: how many people and how many dollars.
 * Shown in both distribution modes — it is the one place where "by score" and "by level" can be
 * compared on the same terms. Both rows come straight from the server's level summary.
 */
export const LevelSummaryTable = ({ levels, levelSummary }: LevelSummaryTableProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const summaryByLevelId = new Map(levelSummary.map((entry) => [entry.levelId, entry]));

    return (
        <div
            className={
                'scrollbar-glass border-base-100 max-w-full overflow-x-auto rounded-[12px] border bg-white'
            }
        >
            <table
                className={`w-full ${COMPENSATION_CAPTION}`}
                data-testid={'compensation-level-summary'}
            >
                {/* The table's own name. Its header row is a row of level icons and the two row
                    headers only say "Nombre de personnes" and "Montant total" — read out of
                    context, neither says what the table counts. */}
                <caption className={'sr-only'}>
                    {t('compensation.strategyCard.summaryTable.caption')}
                </caption>

                <thead>
                    <tr className={'bg-base-25'}>
                        <th
                            scope={'col'}
                            className={'px-3 py-1.5 text-left'}
                        >
                            <span className={'sr-only'}>
                                {t('compensation.strategyCard.summaryTable.levelColumn')}
                            </span>
                        </th>

                        {levels.map((level) => (
                            <th
                                key={level.id}
                                scope={'col'}
                                className={'px-3 py-1.5 text-center'}
                            >
                                <span className={'inline-flex flex-col items-center gap-1'}>
                                    <LevelIcon level={level} />

                                    <Text
                                        weight={'semibold'}
                                        color={Colors.BASE_500}
                                        align={'center'}
                                        className={'block max-w-[88px] text-[11px]'}
                                    >
                                        {getLocalizedText(level.label)}
                                    </Text>
                                </span>
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    <tr className={'border-base-100 border-t'}>
                        <th
                            scope={'row'}
                            className={'px-3 py-1 text-left font-normal whitespace-nowrap'}
                        >
                            <Text
                                color={Colors.BASE_500}
                                className={COMPENSATION_CAPTION}
                            >
                                {t('compensation.strategyCard.summaryTable.headcountRow')}
                            </Text>
                        </th>

                        {levels.map((level) => (
                            <td
                                key={level.id}
                                className={'px-3 py-1 tabular-nums'}
                            >
                                <Text
                                    color={Colors.BASE_500}
                                    align={'center'}
                                    className={COMPENSATION_CAPTION}
                                >
                                    {summaryByLevelId.get(level.id)?.employeeCount ?? 0}
                                </Text>
                            </td>
                        ))}
                    </tr>

                    <tr className={'border-base-100 border-t'}>
                        <th
                            scope={'row'}
                            className={'px-3 py-1.5 text-left font-normal whitespace-nowrap'}
                        >
                            <Text
                                color={Colors.BASE_500}
                                className={COMPENSATION_CAPTION}
                            >
                                {t('compensation.strategyCard.summaryTable.amountRow')}
                            </Text>
                        </th>

                        {levels.map((level) => (
                            <td
                                key={level.id}
                                className={'px-3 py-1.5 whitespace-nowrap tabular-nums'}
                            >
                                <Text
                                    weight={'semibold'}
                                    color={Colors.NAVY}
                                    align={'center'}
                                    className={COMPENSATION_CAPTION}
                                >
                                    {formatCurrency(
                                        summaryByLevelId.get(level.id)?.totalAmount ?? 0,
                                        language,
                                    )}
                                </Text>
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
